import { useCustoPessoal } from '@/api/rateios';
import { ErroLista } from '@/components/cargos';
import { MonthPicker } from '@/components/ui/month-picker';
import { formatarDinheiro } from '@/lib/format';
import { BotaoRelatorios } from '@/relatorios';

const COMPETENCIA = /^\d{4}-(0[1-9]|1[0-2])$/;

/** Busca da URL: `comp` = AAAA-MM. Valor invalido e descartado. */
export interface BuscaCustoPessoal {
  comp?: string;
}

export function lerBuscaCustoPessoal(bruto: Record<string, unknown>): BuscaCustoPessoal {
  const comp = typeof bruto.comp === 'string' && COMPETENCIA.test(bruto.comp) ? bruto.comp : undefined;
  return comp ? { comp } : {};
}

/** Mes atual (AAAA-MM), padrao da tela de custo de pessoal. */
export function competenciaAtual(agora: Date = new Date()): string {
  return `${agora.getFullYear()}-${String(agora.getMonth() + 1).padStart(2, '0')}`;
}

const TH = 'px-3 py-2.5 font-medium';
const TD = 'px-3 py-3';

/** Relatorio "Custo de pessoal por centro": so totais (nenhum salario individual). Os numeros vem prontos da API. */
export function CustoPessoalPorCentro({ busca, onMudarCompetencia }: { busca: BuscaCustoPessoal; onMudarCompetencia: (comp: string) => void }) {
  const comp = busca.comp ?? competenciaAtual();
  const consulta = useCustoPessoal(comp);
  const dados = consulta.data;

  return (
    <section aria-label="Custo de pessoal por centro" className="mx-auto flex w-full max-w-[1100px] flex-col gap-5 pb-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-small text-ink-muted">
            Custos <span aria-hidden="true">/</span> Custo de pessoal
          </div>
          <h1 className="mt-1 text-display font-semibold tracking-[-0.01em]">Custo de pessoal por centro</h1>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="cp-comp" className="text-small text-ink-secondary">
            Competência
          </label>
          <MonthPicker id="cp-comp" value={comp} onChange={onMudarCompetencia} />
        </div>
        <BotaoRelatorios tela="custo-pessoal" params={{ competencia: comp }} />
      </div>

      <p className="text-small text-ink-secondary">Só totais: nenhum salário individual.</p>

      <div className="rounded-2xl border border-border bg-card">
        {consulta.isError && !dados && (
          <div className="p-4">
            <ErroLista erro={consulta.error} onTentar={() => void consulta.refetch()} titulo="Não foi possível carregar o custo de pessoal" />
          </div>
        )}
        {!consulta.isError && !dados && <p role="status" className="px-5 py-10 text-center text-small text-ink-muted">Carregando custo de pessoal…</p>}
        {dados && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] border-collapse text-small" aria-label="Custo de pessoal por centro">
              <thead>
                <tr className="bg-surface-muted text-left text-caption text-ink-muted">
                  <th scope="col" className={TH}>Centro</th>
                  <th scope="col" className={`${TH} text-right`}>Pessoas</th>
                  <th scope="col" className={`${TH} text-right`}>Terceirizados</th>
                  <th scope="col" className={`${TH} text-right`}>Total R$</th>
                </tr>
              </thead>
              <tbody>
                {dados.centros.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-5 py-8 text-center text-ink-muted">
                      Nenhum custo de pessoal apropriado a centro nesta competência.
                    </td>
                  </tr>
                )}
                {dados.centros.map((c) => (
                  <tr key={c.centroId} data-testid="linha-centro-pessoal" className="border-t border-line-soft">
                    <td className={TD}>
                      <span className="font-mono text-ink-secondary">{c.codigo}</span> {c.nome}
                    </td>
                    <td className={`${TD} tabular text-right`}>{c.pessoas}</td>
                    <td className={`${TD} tabular text-right`}>{c.terceirizados}</td>
                    <td className={`${TD} tabular text-right`}>{formatarDinheiro(c.total)}</td>
                  </tr>
                ))}
                <tr data-testid="linha-sem-centro" className="border-t border-line-soft text-ink-secondary">
                  <td className={TD}>Sem centro</td>
                  <td className={`${TD} tabular text-right`}>{dados.semCentro.pessoas}</td>
                  <td className={`${TD} tabular text-right`}>—</td>
                  <td className={`${TD} tabular text-right`}>{formatarDinheiro(dados.semCentro.total)}</td>
                </tr>
                <tr data-testid="linha-total-pessoal" className="border-t-2 border-border font-semibold">
                  <td className={TD}>Total</td>
                  <td className={`${TD} tabular text-right`}>{dados.pessoas}</td>
                  <td className={`${TD} tabular text-right`}>—</td>
                  <td className={`${TD} tabular text-right`} data-testid="total-custo-pessoal">{formatarDinheiro(dados.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
